<template>
  <section class="yesterday-report-view">
    <template v-for="section in sections" :key="section.title">
      <TeamTableView
        v-if="section.type === 'team_table'"
        :team-table="section.teamTable"
        :hide-empty-rows="section.hideEmptyRows === true"
        :hours-decimals="section.hoursDecimals || 1"
      />
      <section
        v-else
        class="mode-card mode-card--accordion"
      >
        <button
          class="accordion-header"
          type="button"
          :aria-expanded="isSectionExpanded(section)"
          @click="toggleSection(section)"
        >
          <b-icon
            :icon="isSectionExpanded(section) ? 'chevron-down' : 'chevron-right'"
            size="is-small"
            class="accordion-chevron"
          />
          <span class="section-label">{{ section.title }}</span>
        </button>

        <div v-show="isSectionExpanded(section)" class="accordion-body">
          <!-- KAN-103 (LWD v2): a lead line, then sub-lists (Follow-ups / Watch, People-owned work /
               Tool-generated), each with its empty line and "how many more" line. -->
          <template v-if="section.type === 'groups'">
            <p v-if="section.lead" class="mode-text section-lead">{{ section.lead }}</p>
            <div v-for="group in section.groups" :key="`${section.title}-${group.heading}`" class="section-group">
              <h4 class="section-group__heading">{{ group.heading }}</h4>
              <ul v-if="group.items.length" class="mode-list">
                <li
                  v-for="(item, index) in group.items"
                  :key="`${group.heading}-${index}`"
                  v-html="linkifyTicketRefs(item)"
                  @click="onItemClick"
                />
              </ul>
              <p v-else-if="group.empty" class="mode-text">{{ group.empty }}</p>
              <!-- Every item past the top ones (KAN-103 show all), behind a toggle. v-show keeps
                   the rows in the page, so Copy report includes them open or not. -->
              <template v-if="restCount(group)">
                <button
                  type="button"
                  class="section-group__toggle"
                  :aria-expanded="isRestOpen(section, group)"
                  @click="toggleRest(section, group)"
                >
                  {{ isRestOpen(section, group) ? 'Hide' : `Show all ${restCount(group)} more ${group.restNoun || ''}`.trim() }}
                </button>
                <ul v-if="group.rest && group.rest.length" v-show="isRestOpen(section, group)" class="mode-list section-group__rest">
                  <li
                    v-for="(item, index) in group.rest"
                    :key="`${group.heading}-rest-${index}`"
                    v-html="linkifyTicketRefs(item)"
                    @click="onItemClick"
                  />
                </ul>
                <div v-if="group.restTable" v-show="isRestOpen(section, group)" class="section-table-scroll section-group__rest">
                  <table class="section-table">
                    <thead>
                      <tr>
                        <th v-for="column in group.restTable.columns" :key="column">{{ column }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIndex) in group.restTable.rows" :key="`${group.heading}-rest-${rowIndex}`">
                        <td v-for="(cell, index) in row" :key="index">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
              <p v-else-if="group.more" class="mode-text section-group__more">{{ group.more }}</p>
              <!-- A count that stands in for a list (Watch: tickets waiting on the client). -->
              <p v-if="group.note" class="mode-text section-group__more">{{ group.note }}</p>
            </div>
          </template>

          <!-- KAN-103 (LWD v2): a plain table (Tools), numbers as the backend computed them. -->
          <template v-else-if="section.type === 'table'">
            <div class="section-table-scroll">
              <table class="section-table">
                <thead>
                  <tr>
                    <th v-for="(column, index) in section.columns" :key="column" :class="{ 'is-numeric': index > 0 }">{{ column }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in section.rows" :key="`${section.title}-${rowIndex}`" :class="{ 'section-table__total': row.total }">
                    <td v-for="(cell, index) in row.cells" :key="index" :class="{ 'is-numeric': index > 0 }">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="section.note" class="mode-text section-group__more">{{ section.note }}</p>
          </template>

          <p
            v-else-if="section.type === 'text'"
            class="mode-text"
            v-html="linkifyTicketRefs(section.text)"
            @click="onItemClick"
          />

          <ul v-else class="mode-list">
            <li
              v-for="(item, index) in section.items"
              :key="`${section.title}-${index}`"
              v-html="linkifyTicketRefs(item)"
              @click="onItemClick"
            />
          </ul>

          <dl v-if="section.dataContextItems && section.dataContextItems.length" class="section-data-context">
            <div v-for="item in section.dataContextItems" :key="item.label" class="section-data-context__item">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import { ticketRefClass } from '@/utils/visitedTickets'
import TeamTableView from '@/components/roster/ask/TeamTableView.vue'

export default {
  name: 'YesterdayReportView',
  components: {
    TeamTableView,
  },
  props: {
    sections: {
      type: Array,
      default: () => [],
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  data() {
    return {
      expandedSections: {},
      openRest: {},
    }
  },
  methods: {
    // Sections open by default: Summary (v1), or any section marked expanded (v2 opens all of them).
    isSectionExpanded(section) {
      const title = section.title
      if (this.expandedSections[title] !== undefined) return this.expandedSections[title]
      return section.expanded === true || title === 'Summary'
    },
    toggleSection(section) {
      this.expandedSections = {
        ...this.expandedSections,
        [section.title]: !this.isSectionExpanded(section),
      }
    },
    restCount(group) {
      if (group.rest && group.rest.length) return group.rest.length
      return group.restTable && group.restTable.rows ? group.restTable.rows.length : 0
    },
    isRestOpen(section, group) {
      return this.openRest[`${section.title}|${group.heading}`] === true
    },
    toggleRest(section, group) {
      const key = `${section.title}|${group.heading}`
      this.openRest = { ...this.openRest, [key]: !this.openRest[key] }
    },
    linkifyTicketRefs(text) {
      if (!text || !this.ticketRefs.length) return this.escapeHtml(text)
      const ticketNbrs = this.ticketRefs.map((t) => t.ticketNbr).filter(Boolean)
      if (!ticketNbrs.length) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const pattern = new RegExp(`\\b(${ticketNbrs.map(this.escapeRegex).join('|')})\\b`, 'g')
      return escaped.replace(
        pattern,
        (match) => `<span class="${ticketRefClass(match)}" data-ticket-nbr="${match}" role="button" tabindex="0">${match}</span>`
      )
    },
    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    onItemClick(event) {
      const ticketNbr = event.target?.dataset?.ticketNbr
      if (ticketNbr) {
        this.$emit('ticket-ref-clicked', ticketNbr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.yesterday-report-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
}

.mode-card--accordion {
  padding: 0;
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: $space-3;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: $space-2;

  &:hover {
    background: $color-surface-alt;
  }
}

.accordion-body {
  padding: 0 $space-3 $space-3;
}

.accordion-chevron {
  flex-shrink: 0;
  color: $color-text-muted;
}

.section-label {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.mode-text {
  margin: 0;
  white-space: pre-line;
}

.mode-list {
  margin: 0;
  padding-left: 1.2rem;
  list-style-type: disc;
}

.mode-list li + li {
  margin-top: $space-2;
}

:deep(.ticket-ref) {
  color: $color-primary-dark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }

  &.ticket-ref--visited {
    color: $color-text-muted;
  }
}

.section-lead {
  margin-bottom: $space-3;
  color: $color-text-muted;
}

.section-group__toggle {
  margin-top: $space-2;
  padding: 0;
  border: 0;
  background: none;
  color: $color-primary-dark;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }
}

.section-group__rest {
  margin-top: $space-2;
}

.section-group + .section-group {
  margin-top: $space-4;
}

.section-group__heading {
  margin: 0 0 $space-2;
  font-size: $font-size-sm;
  font-weight: 700;
  color: $color-text-strong;
}

.section-group__more {
  margin-top: $space-2;
  color: $color-text-muted;
}

.section-table-scroll {
  overflow-x: auto;
}

.section-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: $space-1 $space-3 $space-1 0;
    text-align: left;
    border-bottom: 1px solid $color-border;
  }

  th {
    font-size: $font-size-xs;
    font-weight: 700;
    color: $color-text-strong;
  }

  .is-numeric {
    text-align: right;
  }
}

.section-table__total td {
  font-weight: 700;
}

.section-data-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: $space-3 0 0;
  padding-top: $space-3;
  border-top: 1px solid $color-border;
}

.section-data-context__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.section-data-context__item dt {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.section-data-context__item dd {
  margin: 0;
}

@media (max-width: 900px) {
  .section-data-context {
    grid-template-columns: 1fr;
  }
}
</style>
